import React, { useMemo } from 'react';
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Calculator, Users, Search, RefreshCw,
  List, BarChart2, FileText,
  ChevronLeft, ChevronRight,
  AlertTriangle, Clock, CalendarX, Timer, TrendingUp,
} from 'lucide-react';
import { STATUS_CONFIG, fmtHours } from '../../utils/attendanceUtils';
import AttendanceDetailTable from './AttendanceDetailTable';
import AttendanceSummaryTable from './AttendanceSummaryTable';

const PointagesResultsSection = ({
  results,
  statusFilter, setStatusFilter,
  statusCounts,
  resultStats,
  viewMode, setViewMode,
  filteredResults,
  paginatedResults,
  tableSearch, setTableSearch,
  handleCalculate, calculating,
  setReportDialog,
  absenceReasons,
  openAssignDialog, openManualPunchDialog, openAbsenceDialog,
  openMenuId, setOpenMenuId,
  summaryRows,
  expandedEmployees, toggleEmployeeExpand,
  runCalculation,
  dateFrom, dateTo,
  currentPage, setCurrentPage,
  pageSize, setPageSize,
  totalPages,
}) => {
  const { t } = useTranslation();

  const insights = useMemo(() => {
    const employees = new Set(results.map(r => r.employee_id)).size;
    const presentLike = results.filter(r => ['present', 'late', 'early_departure', 'late_early_departure', 'missing_exit'].includes(r.status)).length;
    const late = results.filter(r => r.status === 'late' || r.status === 'late_early_departure').length;
    const noSchedule = results.filter(r => r.status === 'no_schedule').length;
    const anomalyCount = results.filter(r => [
      'late', 'early_departure', 'late_early_departure',
      'absent', 'absence_justifiee', 'missing_exit', 'no_schedule',
    ].includes(r.status)).length;
    const worked = results.reduce((sum, r) => sum + (r.worked_hours || 0), 0);
    const overtime = results.reduce((sum, r) => sum + (r.overtime_hours || 0), 0);
    const denominator = results.filter(r => r.status !== 'day_off' && r.status !== 'public_holiday' && r.status !== 'no_schedule').length;
    const presenceRate = denominator ? Math.round((presentLike / denominator) * 100) : null;
    return { employees, presentLike, late, noSchedule, anomalyCount, worked, overtime, presenceRate };
  }, [results]);

  return (
  <div className="space-y-4">
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
      <div className="rounded-lg border bg-card px-3 py-3">
        <p className="text-xs text-muted-foreground">{t('attComp.insights.records')}</p>
        <p className="mt-1 text-2xl font-semibold">{results.length}</p>
      </div>
      <div className="rounded-lg border bg-card px-3 py-3">
        <p className="text-xs text-muted-foreground">{t('attComp.insights.employees')}</p>
        <p className="mt-1 text-2xl font-semibold">{insights.employees}</p>
      </div>
      <div className="rounded-lg border bg-card px-3 py-3">
        <p className="text-xs text-muted-foreground">{t('attComp.insights.presenceRate')}</p>
        <p className="mt-1 text-2xl font-semibold text-green-500">{insights.presenceRate == null ? '—' : `${insights.presenceRate}%`}</p>
      </div>
      <button
        type="button"
        onClick={() => setStatusFilter(statusFilter === 'anomalies' ? 'all' : 'anomalies')}
        className={`rounded-lg border px-3 py-3 text-left transition-colors ${statusFilter === 'anomalies' ? 'border-orange-500 bg-orange-500/10 text-orange-500' : 'bg-card hover:bg-accent'}`}
      >
        <p className="flex items-center gap-1 text-xs text-muted-foreground"><AlertTriangle className="h-3.5 w-3.5" /> {t('attComp.insights.anomalies')}</p>
        <p className="mt-1 text-2xl font-semibold">{insights.anomalyCount}</p>
      </button>
      <button
        type="button"
        onClick={() => setStatusFilter(statusFilter === 'no_schedule' ? 'all' : 'no_schedule')}
        className={`rounded-lg border px-3 py-3 text-left transition-colors ${statusFilter === 'no_schedule' ? 'border-muted-foreground bg-muted text-foreground' : 'bg-card hover:bg-accent'}`}
      >
        <p className="flex items-center gap-1 text-xs text-muted-foreground"><CalendarX className="h-3.5 w-3.5" /> {t('attComp.insights.noSchedule')}</p>
        <p className="mt-1 text-2xl font-semibold">{insights.noSchedule}</p>
      </button>
      <div className="rounded-lg border bg-card px-3 py-3">
        <p className="flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5" /> {t('attComp.insights.late')}</p>
        <p className="mt-1 text-2xl font-semibold text-orange-500">{insights.late}</p>
      </div>
      <div className="rounded-lg border bg-card px-3 py-3">
        <p className="flex items-center gap-1 text-xs text-muted-foreground"><Timer className="h-3.5 w-3.5" /> {t('attComp.insights.worked')}</p>
        <p className="mt-1 text-2xl font-semibold">{fmtHours(Math.round(insights.worked * 100) / 100)}</p>
      </div>
      <div className="rounded-lg border bg-card px-3 py-3">
        <p className="flex items-center gap-1 text-xs text-muted-foreground"><TrendingUp className="h-3.5 w-3.5" /> {t('attComp.insights.overtime')}</p>
        <p className="mt-1 text-2xl font-semibold text-purple-500">{fmtHours(Math.round(insights.overtime * 100) / 100)}</p>
      </div>
    </div>

    {/* Status bar */}
    <div className="flex flex-wrap gap-3">
      <button
        onClick={() => setStatusFilter('all')}
        className={`flex items-center gap-3 p-3 rounded-lg border transition-all min-w-[100px]
          ${statusFilter === 'all'
            ? 'ring-2 ring-primary bg-primary/10 text-primary shadow-sm border-primary/40'
            : 'bg-card text-foreground hover:shadow-sm border-border'}`}
      >
        <Users className="h-5 w-5 shrink-0" />
        <div className="text-left">
          <p className="text-lg font-bold leading-none">{results.length}</p>
          <p className="text-xs mt-0.5">{t("attComp.results.showAll")}</p>
        </div>
      </button>

      {Object.entries(statusCounts).map(([status, count]) => {
        const cfg  = STATUS_CONFIG[status] || { labelKey: null, color: 'bg-muted text-foreground' };
        const Icon = cfg.icon;
        const label = cfg.labelKey ? t(cfg.labelKey) : status;
        return (
          <button key={status}
            onClick={() => setStatusFilter(statusFilter === status ? 'all' : status)}
            className={`flex items-center gap-3 p-3 rounded-lg border transition-all min-w-[100px]
              ${statusFilter === status ? 'ring-2 ring-primary shadow-sm' : 'hover:shadow-sm'}
              ${cfg.color}`}
          >
            {Icon && <Icon className="h-5 w-5 shrink-0" />}
            <div className="text-left">
              <p className="text-lg font-bold leading-none">{count}</p>
              <p className="text-xs mt-0.5">{label}</p>
            </div>
          </button>
        );
      })}
    </div>

    {/* Results card */}
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Calculator className="h-4 w-4" />
              {t("attComp.results.title")}
              <Badge variant="secondary">{viewMode === 'summary' ? summaryRows.length : filteredResults.length}</Badge>
            </CardTitle>
            {resultStats && (
              <CardDescription>
                {t("attComp.results.createdUpdated", { created: resultStats.total_created, updated: resultStats.total_updated })}
                {resultStats.total_errors > 0 && (
                  <span className="text-red-500 ml-1">· {t("attComp.results.errors", { count: resultStats.total_errors })}</span>
                )}
              </CardDescription>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">

            {/* View mode toggle */}
            <div className="flex rounded-lg border overflow-hidden">
              <button onClick={() => setViewMode('detail')}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium transition-colors
                  ${viewMode === 'detail' ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:bg-accent'}`}>
                <List className="h-3.5 w-3.5" /> {t("attComp.results.detail")}
              </button>
              <button onClick={() => setViewMode('summary')}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium transition-colors border-l
                  ${viewMode === 'summary' ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:bg-accent'}`}>
                <BarChart2 className="h-3.5 w-3.5" /> {t("attComp.results.summary")}
              </button>
            </div>

            {viewMode === 'detail' && (
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-44 h-9">
                  <SelectValue placeholder={t("attComp.results.allStatuses")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("attComp.results.allStatuses")}</SelectItem>
                  <SelectItem value="anomalies">{t("attComp.results.anomaliesOnly")}</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                    <SelectItem key={key} value={key}>{t(cfg.labelKey)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <div className="relative">
              <Search className="absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
              <Input placeholder={t("attComp.searchPlaceholder")} className="pl-8 h-9 w-48"
                value={tableSearch} onChange={e => setTableSearch(e.target.value)} />
            </div>

            <Button variant="outline" size="sm" onClick={handleCalculate} disabled={calculating} className="gap-1 h-9">
              <RefreshCw className={`h-4 w-4 ${calculating ? 'animate-spin' : ''}`} />
              {t("attComp.results.recalc")}
            </Button>

            <Button variant="outline" size="sm" onClick={() => setReportDialog(true)} className="gap-1 h-9">
              <FileText className="h-4 w-4" /> {t("attComp.results.reports")}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <div className="overflow-x-auto">
          {viewMode === 'detail' && (
            <AttendanceDetailTable
              paginatedResults={paginatedResults}
              absenceReasons={absenceReasons}
              openAssignDialog={openAssignDialog}
              openManualPunchDialog={openManualPunchDialog}
              openAbsenceDialog={openAbsenceDialog}
              openMenuId={openMenuId}
              setOpenMenuId={setOpenMenuId}
            />
          )}

          {viewMode === 'summary' && (
            <AttendanceSummaryTable
              summaryRows={summaryRows}
              absenceReasons={absenceReasons}
              expandedEmployees={expandedEmployees}
              toggleEmployeeExpand={toggleEmployeeExpand}
              runCalculation={runCalculation}
              calculating={calculating}
              dateFrom={dateFrom}
              dateTo={dateTo}
            />
          )}
        </div>

        {viewMode === 'detail' && filteredResults.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/50">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>
                {t("attComp.results.rangeOf", { from: ((currentPage - 1) * pageSize) + 1, to: Math.min(currentPage * pageSize, filteredResults.length), total: filteredResults.length })}
              </span>
              <Select value={String(pageSize)} onValueChange={v => { setPageSize(Number(v)); setCurrentPage(1); }}>
                <SelectTrigger className="h-7 w-20 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">{t("attComp.results.perPage", { count: 25 })}</SelectItem>
                  <SelectItem value="50">{t("attComp.results.perPage", { count: 50 })}</SelectItem>
                  <SelectItem value="100">{t("attComp.results.perPage", { count: 100 })}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" className="h-7 px-2"
                disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm px-2">{t("common.pageOf", { current: currentPage, total: totalPages })}</span>
              <Button variant="outline" size="sm" className="h-7 px-2"
                disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  </div>
  );
};

export default PointagesResultsSection;
